import { useState, useRef, useEffect } from "react"
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert, List, ListItem  } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { API_URL, SCIENCEBASE_API_URL } from "../../config/config.data";
import datasets from "../../config/entities/entity.datasets";
import updateFrequencies from "../../config/entities/entity.updateFrequencies";

export const AddScienceBaseRecord = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [textBoxValue, setTextBoxValue] = useState("");

  const handleSetTextBoxValue = (e) => setTextBoxValue(e.target.value);

  // useRef to make sure the timerQueue state is up to date in any functions that use it
  const [timerQueue, setTimerQueue] = useState([]);
  const snackbarRef = useRef(timerQueue);
  snackbarRef.current = timerQueue;

  useEffect(() => {
    return () => timerQueue.forEach(({ timerId }) => clearTimeout(timerId));
  }, []);

  const enqueue = (id) => {
    const timerId = setTimeout(dequeue, 3000, id);
  };

  const dequeue = (id) => setTimerQueue((queue) => queue.filter((el) => el.id !== id));

  const addSnackbarAlert = (id, postResponse) => {
    const messages = snackbarRef.current
    if (postResponse.status === 200) {
      setTimerQueue([...messages, { "message": `Dataset ${id} added successfully`, "severity": "success", "id": id }]);
      enqueue(id)
    } else {
      setTimerQueue([...messages, { "message": `Dataset ${id} failed to add`, "severity": "error", "id": id }]);
      enqueue(id)
    }
  }

  const handleAddScienceBaseRecord = () => {
    setDialogOpen(false);
    const id = textBoxValue;
    axios.get(SCIENCEBASE_API_URL(id))
      .then(({ data }) => {
        const dateObj = new Date(data.dates.filter(date => date.type === "Publication")[0]?.dateString);
        const releaseQuarter = `FY${dateObj?.getFullYear().toString().slice(2)} Q${Math.floor(dateObj?.getMonth() / 3) + 1}`;
        let datasetBody = {
          "name": data.title,
          "description": data.summary,
          "release_quarter": releaseQuarter,
          "citation": data.citation || null,
          "data_access_url": data.link?.url,
          "temporal_coverage_start": data.dates.filter(date => date.type === "Start")[0]?.dateString || null,
          "temporal_coverage_end": data.dates.filter(date => date.type === "End")[0]?.dateString || null,
          "update_detail": data.provenance?.dataSource,
          "doi_url": `https://doi.org/${data.identifiers.filter(identifier => identifier.type === "DOI")[0]?.key.split(":")[1]}`
        }

        if (Object.keys(data).includes("maintenanceUpdateFrequency")) {
          axios.get(`${API_URL}/${updateFrequencies.endpoint}`)
            .then((updateFreqResponse) => {
              let updateFreqID = updateFreqResponse.data.update_frequencies.filter((uf) => uf.name === data.maintenanceUpdateFrequency)[0]?.id
              if (!updateFreqID) {
                axios.post(`${API_URL}/${updateFrequencies.endpoint}`, { "name": data.maintenanceUpdateFrequency })
                  .then((updateFreqPostResponse) => {
                    updateFreqID = updateFreqPostResponse.data.id
                    datasetBody["update_frequency_id"] = updateFreqID;
                    axios.post(`${API_URL}/${datasets.endpoint}`, datasetBody)
                      .then((postResponse) => {
                        addSnackbarAlert(id, postResponse);
                      });
                  });
              } else {
                datasetBody["update_frequency_id"] = updateFreqID;
                axios.post(`${API_URL}/${datasets.endpoint}`, datasetBody)
                  .then((postResponse) => {
                    addSnackbarAlert(id, postResponse);
                  });
              }
            })
        } else {
          axios.post(`${API_URL}/${datasets.endpoint}`, datasetBody)
            .then((postResponse) => {
              addSnackbarAlert(id, postResponse);
            });
        }
      });
  }

  return (
    <div>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{
          color: "primary.contrastText",
          borderColor: "primary.dark",
          "&:hover": {
            borderColor: "primary.dark",
          },
        }}
      >
        Add ScienceBase Record
      </Button>
      <Dialog
        maxWidth="xs"
        TransitionProps={{ onEntered: () => { } }}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Add ScienceBase Record</DialogTitle>
        <DialogContent dividers>
          <Typography>Enter ScienceBase ID:</Typography>
          <TextField
            size="small"
            type="text"
            variant="outlined"
            value={textBoxValue}
            onChange={handleSetTextBoxValue}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAddScienceBaseRecord}
            color="secondary"
            disabled={isNaN(parseInt(textBoxValue, 16))}
            sx={{
              fontWeight: "bold",
              "&.Mui-disabled": {
                color: "primary.dark",
              },
            }}
          >
            Add Dataset
          </Button>
        </DialogActions>
      </Dialog>
      <List
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          margin: "1rem",
          zIndex: 9999,
        }}
      >
        {timerQueue && timerQueue.map((alert, index) =>
          <ListItem>
            <Alert
              severity={alert.severity}
              sx={{
                position: "relative",
                bottom: `${index}rem`,
                right: 0,
                margin: "0.5rem",
              }}
            >
              {alert.message}
            </Alert>
          </ListItem>
        )}
      </List>
    </div>
  )
}